// React
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// Amplify
import { updatePassword } from "aws-amplify/auth";

// Material UI
import {
  Box,
  Button,
  Container,
  InputAdornment,
  Typography,
} from "@mui/material";
import { ArrowBack, LockOutlined } from "@mui/icons-material";

// Styles
import { styles } from "./styles";
import { styles as buttonStyles } from "Common/Styles/ButtonStyles";

//Components
import PasswordField from "Components/PasswordField";
import { FormikTextField, MNButton } from "Common/Components";

// Redux
import { useDispatch } from "react-redux";
import { openSnackbar } from "Store/SnackbarSlice";

// Formik
import { useFormik } from "formik";
import { AuthFooter } from "Components/Authentication/Components/AuthFooter";
import { changePasswordValidationSchema } from "Validation/UserValidation";

const ChangePassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleChangePassword = async (oldPassword, newPassword) => {
    try {
      await updatePassword({ oldPassword, newPassword });
      dispatch(
        openSnackbar({
          message: t("changePassword.passwordChangedSuccess"),
          severity: "success",
        })
      );
      navigate("/");
    } catch (error) {
      dispatch(openSnackbar({ message: error.message, severity: "error" }));
    }
  };

  const handleNavigateBack = () => {
    navigate(-1);
  };

  const formik = useFormik({
    initialValues: {
      oldPassword: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: changePasswordValidationSchema(t),
    onSubmit: (values) => {
      handleChangePassword(values.oldPassword, values.password);
    },
  });

  return (
    <Container
      component="form"
      onSubmit={formik.handleSubmit}
      sx={styles.mainContainer}
    >
      <MNButton
        ariaLabel="buttons.back"
        sx={buttonStyles.backButton}
        onClick={handleNavigateBack}
      >
        <ArrowBack fontSize="small" />
        <Typography>{t("buttons.back")}</Typography>
      </MNButton>
      <Box sx={styles.contentBox}>
        <Typography sx={styles.title}>{t("changePassword.title")}</Typography>

        <Box sx={styles.textFieldsBox}>
          <FormikTextField
            formik={formik}
            name="oldPassword"
            field={{
              type: "password",
              label: t("changePassword.oldPassword"),
              required: false,
              slotProps: {
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined />
                    </InputAdornment>
                  ),
                },
              },
            }}
          />
          <PasswordField
            formik={formik}
            name="password"
            label={t("common.newPassword")}
            autoComplete="new-password"
          />
          <FormikTextField
            formik={formik}
            name="confirmPassword"
            field={{
              type: "password",
              label: t("common.confirmNewPassword"),
              required: false,
              slotProps: {
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined />
                    </InputAdornment>
                  ),
                },
              },
            }}
          />
        </Box>
        <Button type="submit" variant="contained" sx={styles.submitButton}>
          {t("changePassword.submit")}
        </Button>
      </Box>
      <AuthFooter showCopyright={false} />
    </Container>
  );
};

export default ChangePassword;
